import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, PenLine } from "lucide-react";
import axios from "axios";
import { useLang } from "@/i18n";
import ReviewModal from "@/components/ReviewModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Testimonials() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [reviews, setReviews] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/reviews`);
      setReviews(data || []);
    } catch {
      setReviews([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Use live approved reviews; fall back to the curated static ones if none yet.
  const staticReviews = t.testimonials.reviews.map((r) => ({ ...r, rating: 5, photo: null }));
  const display = reviews.length > 0 ? reviews : staticReviews;

  return (
    <section id="testimonials" className="relative py-28 md:py-40 bg-[#080808]" data-testid="testimonials-section">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <div className="mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <span className="text-[#d4af37] uppercase tracking-[0.3em] text-xs flex items-center gap-3">
              <span className="h-px w-10 bg-[#d4af37]" /> {t.testimonials.eyebrow}
            </span>
            <h2 className="mt-5 font-serif-lux font-light text-4xl sm:text-6xl tracking-tight text-white">
              {t.testimonials.titleA} <span className="italic text-[#d4af37]">{t.testimonials.titleB}</span>
            </h2>
          </div>
          <div className="flex flex-col sm:items-end gap-4">
            <div className="flex items-center gap-2 text-[#d4af37]">
              {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={20} fill="#d4af37" />)}
              <span className="text-white/60 ml-2 text-sm">{t.testimonials.rating}</span>
            </div>
            <button
              onClick={() => setOpen(true)}
              data-testid="leave-review-btn"
              className="group inline-flex items-center gap-2 border border-[#d4af37]/40 text-[#d4af37] px-6 py-3 uppercase tracking-[0.15em] text-xs hover:bg-[#d4af37] hover:text-black transition-colors duration-300"
            >
              <PenLine size={15} /> {t.testimonials.leaveReview}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {display.map((r, i) => (
            <motion.blockquote
              key={r.id || r.name + i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="group flex flex-col border border-[#d4af37]/20 p-9 bg-[#050505] hover:-translate-y-1 hover:border-[#d4af37]/50 transition-transform transition-colors duration-400"
              data-testid={`testimonial-${i}`}
            >
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} size={15} className={s < (r.rating || 5) ? "text-[#d4af37]" : "text-white/15"} fill={s < (r.rating || 5) ? "#d4af37" : "transparent"} />
                ))}
              </div>
              {r.photo && (
                <img src={r.photo} alt={`${r.name}'s project`} loading="lazy" className="w-full h-44 object-cover mb-5 border border-[#d4af37]/15" />
              )}
              <Quote className="text-[#d4af37]/50" size={26} />
              <p className="mt-3 font-serif-lux font-light text-lg leading-relaxed text-white/85 italic flex-1">
                &ldquo;{r.text || r.quote}&rdquo;
              </p>
              <footer className="mt-6 pt-5 border-t border-white/10">
                <p className="text-white font-medium">{r.name}</p>
                {(r.location || r.role) && (
                  <p className="text-[#d4af37]/80 text-sm uppercase tracking-[0.12em] mt-1">{r.location || r.role}</p>
                )}
              </footer>
            </motion.blockquote>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {open && <ReviewModal onClose={() => setOpen(false)} onSubmitted={load} />}
      </AnimatePresence>
    </section>
  );
}
